import { ClipboardCheck, Factory, Workflow } from 'lucide-react';

import { defineTour, type TourResolver } from '@nucleus-commerce/admin-kit/tour';
import {
  getAdminV1StoresStoreIdQcTemplates,
  getAdminV1StoresStoreIdWorkflowTemplates,
} from '@nucleus-commerce/api-client/admin';

import { ORDER_DETAIL_ROUTE } from './orders';
import { orderInProduction } from './resolvers';

export const productionBoardTour = defineTour({
  id: 'production.board',
  version: 1,
  group: 'production',
  route: '/_authed/_active/s/$storeId/production',
  scope: 'store',
  requires: 'orders.read',
  icon: Factory,
  steps: [
    { id: 'intro', kind: 'intro' },
    {
      id: 'columns',
      kind: 'explain',
      variants: [
        { target: 'production.board.columns' },
        { target: 'production.board.empty', copy: 'empty' },
      ],
    },
    { id: 'card', kind: 'explain', target: 'production.board.card', optional: true },
    { id: 'label-filter', kind: 'explain', target: 'production.board.label-filter' },
    {
      id: 'search',
      kind: 'action',
      target: 'production.board.search',
      advanceOn: { input: true },
      optional: true,
    },
    { id: 'notifications', kind: 'explain', target: 'shell.notifications' },
  ],
  related: ['production.card', 'workflows.list'],
});

export const productionCardTour = defineTour({
  id: 'production.card',
  version: 1,
  group: 'production',
  route: ORDER_DETAIL_ROUTE,
  scope: 'record',
  resolve: orderInProduction,
  requires: 'orders.read',
  icon: Workflow,
  steps: [
    { id: 'card', kind: 'explain', target: 'orders.detail.production', placement: 'right' },
    {
      id: 'stages',
      kind: 'explain',
      target: 'orders.detail.production.stages',
      placement: 'right',
    },
    {
      id: 'approval',
      kind: 'explain',
      target: 'orders.detail.production.approval',
      optional: true,
    },
    {
      id: 'delay-reason',
      kind: 'explain',
      target: 'orders.detail.production.delay-reason',
      optional: true,
    },
    { id: 'files', kind: 'explain', target: 'orders.detail.production.files', optional: true },
    {
      id: 'change-requests',
      kind: 'explain',
      target: 'orders.detail.production.change-requests',
      placement: 'top',
    },
  ],
  related: ['production.stage-actions'],
});

export const productionStageActionsTour = defineTour({
  id: 'production.stage-actions',
  version: 1,
  group: 'production',
  route: ORDER_DETAIL_ROUTE,
  scope: 'record',
  resolve: orderInProduction,
  requires: 'orders.read',
  icon: Workflow,
  steps: [
    {
      id: 'actions',
      kind: 'explain',
      target: 'orders.detail.production.actions',
      requires: 'orders.write',
    },
    {
      id: 'qc',
      kind: 'explain',
      target: 'orders.detail.production.qc',
      requires: 'orders.write',
      optional: true,
    },
    {
      id: 'send-files',
      kind: 'explain',
      target: 'orders.detail.production.send-files',
      requires: 'orders.write',
      optional: true,
    },
    {
      id: 'parcels',
      kind: 'explain',
      target: 'orders.detail.production.parcels',
      requires: 'orders.write',
      optional: true,
    },
    {
      id: 'due-date',
      kind: 'explain',
      target: 'orders.detail.production.due-date',
      requires: 'orders.write',
      optional: true,
    },
    {
      id: 'switch',
      kind: 'explain',
      target: 'orders.detail.production.switch',
      requires: 'orders.write',
      optional: true,
    },
  ],
  related: ['workflows.editor', 'production.board'],
});

/** The default template is the one every paid order follows; any other will do. */
const defaultWorkflowTemplate: TourResolver = {
  key: 'default-workflow-template',
  thing: 'things.workflowTemplate',
  load: async ({ storeId }) => {
    if (!storeId) return null;
    const { items } = await getAdminV1StoresStoreIdWorkflowTemplates(
      { storeId },
      { summary: 'true' },
    );
    const template = items.find((item) => item.isDefault) ?? items[0];
    return template ? { storeId, templateId: template.id } : null;
  },
};

const firstQcChecklist: TourResolver = {
  key: 'first-qc-checklist',
  thing: 'things.qcChecklist',
  load: async ({ storeId }) => {
    if (!storeId) return null;
    const { items } = await getAdminV1StoresStoreIdQcTemplates({ storeId });
    const checklist = items[0];
    return checklist ? { storeId, checklistId: checklist.id } : null;
  },
};

const WORKFLOW_EDITOR_ROUTE = '/_authed/_active/s/$storeId/settings/workflows/$templateId';

export const workflowsListTour = defineTour({
  id: 'workflows.list',
  version: 1,
  group: 'production',
  route: '/_authed/_active/s/$storeId/settings/workflows/',
  scope: 'store',
  requires: 'settings.read',
  icon: Workflow,
  steps: [
    { id: 'intro', kind: 'intro' },
    {
      id: 'read-only',
      kind: 'explain',
      target: 'settings.read-only',
      optional: true,
      waitMs: 1000,
    },
    {
      id: 'list',
      kind: 'explain',
      variants: [
        { target: 'workflows.list.table' },
        { target: 'workflows.list.empty', copy: 'empty' },
      ],
    },
    {
      id: 'row-menu',
      kind: 'explain',
      target: 'workflows.list.row-menu',
      requires: 'settings.write',
      optional: true,
    },
    { id: 'qc-link', kind: 'explain', target: 'workflows.list.qc-link' },
    {
      id: 'new',
      kind: 'action',
      target: 'workflows.list.new',
      requires: 'settings.write',
      optional: true,
    },
  ],
  related: ['workflows.editor', 'qc.list'],
});

export const workflowEditorTour = defineTour({
  id: 'workflows.editor',
  version: 1,
  group: 'production',
  route: WORKFLOW_EDITOR_ROUTE,
  scope: 'record',
  resolve: defaultWorkflowTemplate,
  requires: 'settings.read',
  icon: Workflow,
  steps: [
    { id: 'name', kind: 'explain', target: 'workflows.editor.name' },
    { id: 'stages', kind: 'explain', target: 'workflows.editor.stages', placement: 'right' },
    {
      id: 'reorder',
      kind: 'explain',
      target: 'workflows.editor.stage.reorder',
      requires: 'settings.write',
    },
    {
      id: 'expand',
      kind: 'action',
      target: 'workflows.editor.stage.expand',
      advanceOn: { appears: 'workflows.editor.stage.type' },
    },
    {
      id: 'add-stage',
      kind: 'explain',
      target: 'workflows.editor.add-stage',
      requires: 'settings.write',
      optional: true,
    },
    {
      id: 'save',
      kind: 'explain',
      target: 'workflows.editor.save',
      requires: 'settings.write',
      optional: true,
    },
  ],
  related: ['workflows.stage-settings'],
});

export const workflowStageSettingsTour = defineTour({
  id: 'workflows.stage-settings',
  version: 1,
  group: 'production',
  route: WORKFLOW_EDITOR_ROUTE,
  scope: 'record',
  resolve: defaultWorkflowTemplate,
  requires: 'settings.read',
  icon: Workflow,
  steps: [
    {
      id: 'open',
      kind: 'action',
      target: 'workflows.editor.stage.expand',
      advanceOn: { appears: 'workflows.editor.stage.type' },
    },
    { id: 'type', kind: 'explain', target: 'workflows.editor.stage.type' },
    { id: 'duration', kind: 'explain', target: 'workflows.editor.stage.duration' },
    {
      id: 'approves-design',
      kind: 'explain',
      target: 'workflows.editor.stage.approves-design',
      optional: true,
      waitMs: 1000,
    },
    {
      id: 'qc-checklist',
      kind: 'explain',
      target: 'workflows.editor.stage.qc-checklist',
      optional: true,
      waitMs: 1000,
    },
    { id: 'email', kind: 'explain', target: 'workflows.editor.stage.email', placement: 'top' },
  ],
  related: ['qc.list', 'production.stage-actions'],
});

export const qcListTour = defineTour({
  id: 'qc.list',
  version: 1,
  group: 'production',
  route: '/_authed/_active/s/$storeId/settings/qc-checklists/',
  scope: 'store',
  requires: 'settings.read',
  icon: ClipboardCheck,
  steps: [
    { id: 'intro', kind: 'intro' },
    {
      id: 'list',
      kind: 'explain',
      variants: [{ target: 'qc.list.table' }, { target: 'qc.list.empty', copy: 'empty' }],
    },
    {
      id: 'new',
      kind: 'action',
      target: 'qc.list.new',
      requires: 'settings.write',
      optional: true,
    },
  ],
  related: ['qc.editor'],
});

export const qcEditorTour = defineTour({
  id: 'qc.editor',
  version: 1,
  group: 'production',
  route: '/_authed/_active/s/$storeId/settings/qc-checklists/$checklistId',
  scope: 'record',
  resolve: firstQcChecklist,
  requires: 'settings.read',
  icon: ClipboardCheck,
  steps: [
    { id: 'name', kind: 'explain', target: 'qc.editor.name' },
    { id: 'questions', kind: 'explain', target: 'qc.editor.questions', placement: 'right' },
    {
      id: 'add-question',
      kind: 'explain',
      target: 'qc.editor.add-question',
      requires: 'settings.write',
      optional: true,
    },
    { id: 'preview', kind: 'explain', target: 'qc.editor.preview', placement: 'left' },
    {
      id: 'save',
      kind: 'explain',
      target: 'qc.editor.save',
      requires: 'settings.write',
      optional: true,
    },
  ],
  related: ['workflows.stage-settings'],
});
